import { storeToRefs } from 'pinia';
import type { Benefit, SubscriptionPlanConfig } from '~/app/auth/enums/TenantSubscriptionPlan';
import { getPlanCommercialName } from '~/app/auth/enums/PlanCommercialName';
import type { DenialReason, MenuVisibilityEntry } from '~/stores/menuVisibilityStore';

export type UpsellPlanTier = 'essential' | 'enterprise';

/** Surface an upsell restriction is shown on, sent as the `type` tracking property. */
export type UpsellTriggerType = 'page' | 'panel' | 'nav' | 'tabs' | 'context_menu';

export interface UpsellPlanInfo {
    name: string;
    tier: UpsellPlanTier;
    price: string;
}

export default function useUpsellAccess() {
    const { t } = useTranslation();
    const { tenant } = storeToRefs(useAuthStore());
    const menuVisibilityStore = useMenuVisibilityStore();

    const isFreeplanForced = computed(() => {
        try {
            return typeof window !== 'undefined' && localStorage.getItem('freeplan') === 'true';
        } catch {
            return false;
        }
    });

    const canAccessBenefit = (benefit: Benefit): boolean => {
        if (isFreeplanForced.value) return false;
        if (!tenant.value) return true;
        const config = tenant.value.subscriptionPlanConfig;
        if (!config) return true;
        return (config.benefits as readonly string[]).includes(benefit);
    };

    const canAccessPermission = (permissionKey: string): boolean => {
        if (isFreeplanForced.value) return false;
        if (menuVisibilityStore.loaded) {
            return menuVisibilityStore.isAccessible(permissionKey);
        }
        return true;
    };

    const getPermissionEntry = (permissionKey: string): MenuVisibilityEntry | undefined => {
        return menuVisibilityStore.getEntry(permissionKey);
    };

    const isUpsellRestricted = (permissionKey: string): boolean => {
        if (isFreeplanForced.value) return true;
        // Read `loaded` so this stays a reactive dependency: the sidebar badges are
        // evaluated while the menu-visibility map is still being fetched (first login),
        // and must re-evaluate once it resolves instead of staying hidden until a refresh.
        if (!menuVisibilityStore.loaded) return false;
        const entry = menuVisibilityStore.getEntry(permissionKey);
        if (!entry) return false;
        return !entry.accessible && entry.reason === 'plan_restriction';
    };

    const getDenialReason = (permissionKey: string): DenialReason | null => {
        return menuVisibilityStore.getDenialReason(permissionKey);
    };

    /**
     * Whether a sidebar entry must be hidden outright (as opposed to shown with an upsell
     * badge). Mirrors the `v-can` directive's removal branch but stays reactive: the directive
     * is functional and runs once at mount, so an entry denied by `user_permission` (a `user`
     * without the right) stayed visible when the async menu-visibility map resolved after the
     * first render — the flash Simon reported (TDD-3522). Reading `loaded` makes callers
     * re-evaluate once the map lands. `plan_restriction` denials return false: those keep the
     * entry visible with the lightning upsell badge.
     */
    const isMenuEntryDenied = (permissionKey: string): boolean => {
        const { authed } = useAuthStore();
        if (authed?.hasPermission(permissionKey)) return false;
        if (!menuVisibilityStore.loaded) return false;
        const entry = menuVisibilityStore.getEntry(permissionKey);
        if (!entry) return false;
        return !entry.accessible && entry.reason !== 'plan_restriction';
    };

    // Cross-family restriction (TDD-3522): the upsell targets a plan in a different product
    // family, so the modal/inline upsell is shown without the price panel.
    const isCrossFamily = (permissionKey: string): boolean => {
        return menuVisibilityStore.isCrossFamily(permissionKey);
    };

    const getUpsellPlanConfig = (benefit: Benefit): SubscriptionPlanConfig | undefined => {
        if (!tenant.value) return undefined;
        const config = tenant.value.subscriptionPlanConfig;
        if (!config) return undefined;

        // When freeplan is forced, skip the "already has benefit" check
        if (!isFreeplanForced.value && (config.benefits as readonly string[]).includes(benefit)) return undefined;

        const globalConfig = tenant.value.globalSubscriptionPlanConfig;
        return Object.values(globalConfig).find((plan) => (plan.benefits as readonly string[]).includes(benefit) && plan.isCombo === config.isCombo && plan.isActive);
    };

    const getPlanTranslatedName = (planKey: string): string => {
        return t(`plans.${planKey}.title`);
    };

    const upsellPlanNameForBenefit = (benefit: Benefit): string | undefined => {
        const plan = getUpsellPlanConfig(benefit);
        if (!plan?.name) return undefined;
        return getPlanTranslatedName(plan.name);
    };

    const getUpsellTier = (planName: string): UpsellPlanTier => {
        const normalized = planName.replace('combo-', '').toLowerCase();
        if (normalized === 'enterprise') return 'enterprise';
        return 'essential';
    };

    /**
     * Price translation key for a plan. Unlike title/description/usp (keyed on the bare
     * tier), pricing keeps the combo distinction — a combo plan has a different price than
     * its solo counterpart — so combo plans resolve to `upsell.combo-{tier}.price` while
     * solo plans stay on `upsell.{tier}.price`.
     */
    const getUpsellPriceKey = (planName: string): string => {
        const tier = getUpsellTier(planName);
        return planName.startsWith('combo-') ? `combo-${tier}` : tier;
    };

    /**
     * Pricing is exposed through translation keys (upsell.{priceKey}.price) so the
     * product team can update prices via TranslationIO without a code change.
     */
    const getUpsellPrice = (planName: string): string => {
        return t(`upsell.${getUpsellPriceKey(planName)}.price`);
    };

    const upsellPlanNameForPermission = (permissionKey: string): string | undefined => {
        const target = menuVisibilityStore.getTarget(permissionKey);
        if (!target) return undefined;
        return getPlanTranslatedName(target);
    };

    const getUpsellPlanInfoForPermission = (permissionKey: string): UpsellPlanInfo | undefined => {
        const target = menuVisibilityStore.getTarget(permissionKey);
        if (!target) return undefined;

        const tier = getUpsellTier(target);

        return {
            name: getPlanTranslatedName(target),
            tier,
            price: getUpsellPrice(target),
        };
    };

    const getUpsellPlanInfo = (benefit: Benefit): UpsellPlanInfo | undefined => {
        const plan = getUpsellPlanConfig(benefit);
        if (!plan) return undefined;

        const tier = getUpsellTier(plan.name);

        return {
            name: getPlanTranslatedName(plan.name),
            tier,
            price: getUpsellPrice(plan.name),
        };
    };

    /**
     * Entry-level upsell plan the current tenant is pushed towards (e.g. `starter` for a
     * free tenant). Used as the target when no more specific one is available.
     */
    const defaultUpsellPlanId = (): string | null => {
        const upsellPlans = tenant.value?.subscriptionPlanConfig?.upsellPlans as readonly string[] | undefined;
        return upsellPlans?.[0] ?? null;
    };

    /**
     * Resolves the upsell target plan (id + commercial name) for tracking.
     *
     * `plan_id` comes from the menu-visibility `target` when present, else from the plan
     * config (benefit) or the tenant's entry-level upsell plan. `commercial_name` prefers
     * the back-provided `target_label` (TDD-3433) and falls back to the static mapping,
     * which is required for surfaces that open without a 403 (e.g. sidebar clicks).
     */
    const getUpsellTrackingPlan = (source: { permissionKey?: string; benefit?: Benefit }): { planId: string | null; commercialName: string | null } => {
        let planId: string | null = null;
        let commercialName: string | null = null;

        if (source.permissionKey) {
            planId = menuVisibilityStore.getTarget(source.permissionKey);
            commercialName = menuVisibilityStore.getTargetLabel(source.permissionKey);
        } else if (source.benefit) {
            planId = getUpsellPlanConfig(source.benefit)?.name ?? null;
        }

        // Fall back to the tenant's entry-level upsell plan when the source gives no target.
        if (!planId) planId = defaultUpsellPlanId();
        // Resolve the commercial name from the static mapping when the back didn't ship one.
        if (!commercialName) commercialName = getPlanCommercialName(planId);

        return { planId, commercialName };
    };

    /**
     * Fires the unified PostHog event for an upsell exposure. `type` is the surface the
     * restriction is shown on (page | panel | nav | tabs | context_menu) and `label` is
     * the human-readable title of the element that triggered it (nav/sub-menu name, tab
     * label, page title) so we can tell which element drives the most triggers.
     */
    const trackUpsellRestriction = (type: UpsellTriggerType, source: { permissionKey?: string; benefit?: Benefit; label?: string }): void => {
        const { $tracker } = useNuxtApp();
        const { planId, commercialName } = getUpsellTrackingPlan(source);
        $tracker.track('upsell_plan_restriction', {
            type,
            label: source.label ?? null,
            plan_id: planId,
            commercial_name: commercialName,
        });
    };

    return {
        canAccessBenefit,
        canAccessPermission,
        getPermissionEntry,
        isUpsellRestricted,
        isMenuEntryDenied,
        getDenialReason,
        isCrossFamily,
        upsellPlanNameForBenefit,
        upsellPlanNameForPermission,
        getUpsellPlanInfo,
        getUpsellPlanInfoForPermission,
        getUpsellTrackingPlan,
        trackUpsellRestriction,
    };
}
